import React, { Fragment, useCallback, useEffect, useState } from 'react';

import { styled } from '@compiled/react';
import debounce from 'lodash/debounce';
import isBoolean from 'lodash/isBoolean';
import { FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { cssMap } from '@atlaskit/css';
import { Label } from '@atlaskit/form/label/default';
import { CardClient as Client } from '@atlaskit/link-provider';
import { Box, Flex, Inline } from '@atlaskit/primitives/compiled';
import TextArea from '@atlaskit/textarea/text-area';
import Textfield from '@atlaskit/textfield/text-field';
import { token } from '@atlaskit/tokens';
import { ConvoAIResponseState, type ConvoAIStreamError } from '@atlassian/ai-agent-streaming';
import { AIEventsInstrumentationProvider } from '@atlassian/ai-analytics';
import { AIBorder, AIStreamLoading } from '@atlassian/ai-components';
import {
	AIDescriptionButtonWrapper,
	AIDescriptionStatusMessage,
	ITEM_FIELDS as CARD_FIELDS,
	isPrivateLoomImageUrl,
	LabelButton,
	LinkPicker,
	type OnSubmitPayload,
	PanelSubheader,
	useFetchCoverImage,
	useProductDependencies,
} from '@atlassian/company-hub-common';
import { isUnsplashImage } from '@atlassian/company-hub-common/media-components';

import { CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE } from '../../../../../../constants';
import { updateLinkAndConfluencePageId } from '../../../../../../controllers/use-cards-extension-parameters-and-actions';
import type { UpdateCardFormFieldType } from '../../../../../../controllers/use-cards-extension-parameters-and-actions/types';
import {
	type Card,
	CardSize,
	CardsLayout,
	type ResolvedLinkDataObject,
} from '../../../../../../types';

import { messages } from './messages';

const styles = cssMap({
	aiLoadingTextContainer: {
		font: token('font.body.small'),
		color: token('color.text.subtle'),
	},
	fieldWrapperStyles: {
		marginTop: token('space.200'),
	},
});

export type CardFieldProps = {
	cardParameters: Card;
	isDisabled?: boolean;
	layout?: CardsLayout;
	showDescription: boolean;
	showLabel?: boolean;
	size?: CardSize;
	updateCardFormField: UpdateCardFormFieldType;
};

const debouncedParametersUpdate = debounce(
	(
		updateCardFormField,
		inputPropertyToUpdate,
		inputIsUserEditedPropertyToUpdate,
		cardId,
		value,
		createAnalyticsEvent,
		analyticsSource,
		shouldUpdateUserEdited = true,
	) => {
		updateCardFormField(cardId, {
			[inputPropertyToUpdate]: value,
			[inputIsUserEditedPropertyToUpdate]: shouldUpdateUserEdited,
		});

		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'edited',
				actionSubject: inputPropertyToUpdate,
				source: analyticsSource,
			},
		}).fire();
	},
	400,
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RecommendedWrapper = styled.span({
	textTransform: 'none',
	fontWeight: token('font.weight.regular'),
});

const externalSiteMatcher =
	/^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/;

const getFieldsToUpdateOnLinkSelect = async (
	selectedLink: OnSubmitPayload,
	fetchCoverImageUrl: (
		confluencePageId?: string,
		image?: { url?: string },
	) => Promise<string | undefined>,
	cardImage: string | undefined,
	cardTitle: string | undefined,
	cardDescription: string | undefined,
	isTitleUserEdited: boolean,
	isDescriptionUserEdited: boolean,
	submitError?: (error: Error) => void,
): Promise<Partial<Card>> => {
	let fieldsToUpdate: Partial<Card> = updateLinkAndConfluencePageId(selectedLink.url, '', true);

	try {
		const client = new Client();
		const queriedMetadata = (await client.fetchData(selectedLink.url)) as ResolvedLinkDataObject;
		const { name, image, summary } = queriedMetadata?.data || {};
		const { objectId, resourceType } = queriedMetadata?.meta || {};

		fieldsToUpdate = {
			...fieldsToUpdate,
			...updateLinkAndConfluencePageId(selectedLink.url, objectId, true, resourceType),
		};

		if (!cardTitle || isTitleUserEdited === false) {
			fieldsToUpdate[CARD_FIELDS.TITLE] = name;
			fieldsToUpdate[CARD_FIELDS.IS_TITLE_USER_EDITED] = false;
		}
		if (!cardDescription || isDescriptionUserEdited === false) {
			fieldsToUpdate[CARD_FIELDS.DESCRIPTION] = summary;
			fieldsToUpdate[CARD_FIELDS.IS_DESCRIPTION_USER_EDITED] = false;
		}

		// Updating imageSrc and imageAltText
		let newImageSrc: string = cardImage || '';
		const newImageAltText: string = '';
		const coverImage = await fetchCoverImageUrl(objectId, image);
		if (coverImage) {
			const isExternalSiteImage = externalSiteMatcher.test(coverImage);
			if (!isUnsplashImage(coverImage) && !isExternalSiteImage) {
				// For custom uploaded image save it in format `contentId/imageId`
				newImageSrc = `${objectId}/${coverImage}`;
			} else {
				newImageSrc = coverImage;
			}
		}

		// Do not store private Loom CDN thumbnails (they expire via Policy)
		if (isPrivateLoomImageUrl(newImageSrc)) {
			newImageSrc = '';
		}

		fieldsToUpdate = {
			...fieldsToUpdate,
			[CARD_FIELDS.IMAGE_SRC]: newImageSrc,
			[CARD_FIELDS.IMAGE_ALT_TEXT]: newImageAltText,
		};
	} catch (error) {
		submitError?.(error as Error);
	}

	return fieldsToUpdate;
};

export const CardField = ({
	cardParameters,
	updateCardFormField,
	isDisabled,
	showDescription,
	showLabel,
	layout,
	size,
}: CardFieldProps): React.JSX.Element => {
	const {
		cardId,
		link,
		title,
		description,
		label,
		isLabelHidden,
		labelColor,
		isTitleUserEdited,
		isDescriptionUserEdited,
		imageSrc,
		confluencePageId,
		linkResourceType,
	} = cardParameters;
	const { cloudId, submitError } = useProductDependencies();

	const [titleValue, setTitleValue] = useState(title);
	const [descriptionValue, setDescriptionValue] = useState(description);
	const [labelValue, setLabelValue] = useState(label);
	const [isLabelHiddenValue, setIsLabelHiddenValue] = useState(isLabelHidden);
	const [labelColorValue, setLabelColorValue] = useState(labelColor);

	const [isAILoading, setIsAILoading] = useState<boolean | undefined>();
	const [aiDescriptionContent, setAIDescriptionContent] = useState<string | undefined | null>();
	const [aiError, setAIError] = useState<ConvoAIStreamError | undefined>();
	const [aiResponseState, setAIResponseState] = useState<ConvoAIResponseState>();

	const linkId = `link-field-${cardId}`;
	const titleId = `title-field-${cardId}`;
	const descriptionId = `description-field-${cardId}`;
	const labelId = `label-field-${cardId}`;

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleDescriptionChange = useCallback(
		(value: string, aiValue?: boolean) => {
			setDescriptionValue(value);
			debouncedParametersUpdate(
				updateCardFormField,
				CARD_FIELDS.DESCRIPTION,
				CARD_FIELDS.IS_DESCRIPTION_USER_EDITED,
				cardId,
				value,
				createAnalyticsEvent,
				CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE,
				!aiValue,
			);
		},
		[updateCardFormField, cardId, createAnalyticsEvent],
	);

	const handleTitleOnChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const { value } = event.currentTarget;
			setTitleValue(value.trimStart());
			debouncedParametersUpdate(
				updateCardFormField,
				CARD_FIELDS.TITLE,
				CARD_FIELDS.IS_TITLE_USER_EDITED,
				cardId,
				value.trimStart(),
				createAnalyticsEvent,
				CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE,
			);
		},
		[updateCardFormField, cardId, createAnalyticsEvent],
	);

	const handleLabelChange = useCallback(
		(value: string) => {
			setLabelValue(value);
			debouncedParametersUpdate(
				updateCardFormField,
				CARD_FIELDS.LABEL,
				true,
				cardId,
				value,
				createAnalyticsEvent,
				CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE,
			);
		},
		[updateCardFormField, cardId, createAnalyticsEvent],
	);

	const handleIsLabelHiddenChange = useCallback(
		(value: boolean) => {
			setIsLabelHiddenValue(value);
			updateCardFormField(cardId, {
				[CARD_FIELDS.IS_LABEL_HIDDEN]: value,
			});
		},
		[updateCardFormField, cardId],
	);

	const handleLabelColorChange = useCallback(
		(value: string) => {
			setLabelColorValue(value);
			updateCardFormField(cardId, {
				[CARD_FIELDS.LABEL_COLOR]: value,
			});
		},
		[updateCardFormField, cardId],
	);

	const fetchCoverImageUrl = useFetchCoverImage(CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE);

	const handleSelectLink = useCallback(
		async (selectedLink: OnSubmitPayload) => {
			const fieldsToUpdate = await getFieldsToUpdateOnLinkSelect(
				selectedLink,
				fetchCoverImageUrl,
				imageSrc,
				title,
				description,
				isTitleUserEdited || false,
				isDescriptionUserEdited || false,
				submitError,
			);

			updateCardFormField(cardId, fieldsToUpdate);
		},
		[
			cardId,
			imageSrc,
			title,
			description,
			isTitleUserEdited,
			isDescriptionUserEdited,
			fetchCoverImageUrl,
			updateCardFormField,
			submitError,
		],
	);

	const handleClearLink = useCallback(() => {
		const fieldsToClear: Partial<Card> = {
			...updateLinkAndConfluencePageId('', ''),
		};

		if (isTitleUserEdited === false) {
			fieldsToClear[CARD_FIELDS.TITLE] = '';
			fieldsToClear[CARD_FIELDS.IS_TITLE_USER_EDITED] = undefined;
		}

		if (isDescriptionUserEdited === false) {
			fieldsToClear[CARD_FIELDS.DESCRIPTION] = '';
			fieldsToClear[CARD_FIELDS.IS_DESCRIPTION_USER_EDITED] = undefined;
			setIsAILoading(undefined);
		}

		updateCardFormField(cardId, fieldsToClear);
	}, [updateCardFormField, cardId, isTitleUserEdited, isDescriptionUserEdited]);

	useEffect(() => {
		// Sets title from prop
		setTitleValue(title);

		// If user triggers AI, set the the AI-generated content
		if (
			isAILoading &&
			aiDescriptionContent &&
			aiResponseState === ConvoAIResponseState.FinalResponse
		) {
			updateCardFormField(cardId, {
				description: aiDescriptionContent,
				isDescriptionUserEdited: false,
			});

			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'edited',
					actionSubject: CARD_FIELDS.DESCRIPTION,
					source: CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE,
					attributes: {
						isAIGenerated: true,
					},
				},
			}).fire();
			setIsAILoading(false);
		}

		setDescriptionValue(description);

		// Sets AI response loading state to false if AI error
		if (aiError && isBoolean(isAILoading)) {
			setIsAILoading(false);
		}
	}, [
		aiDescriptionContent,
		aiResponseState,
		description,
		title,
		isAILoading,
		aiError,
		updateCardFormField,
		cardId,
		createAnalyticsEvent,
	]);

	return (
		<Fragment>
			<Label htmlFor={linkId}>
				<PanelSubheader showUpdatedStyles>
					<FormattedMessage {...messages.linkFieldLabel} />
				</PanelSubheader>
			</Label>
			<LinkPicker
				cloudId={cloudId}
				link={link}
				id={linkId}
				title={title}
				handleSelectLink={handleSelectLink}
				handleClearLink={handleClearLink}
				isDisabled={isDisabled}
			/>
			{layout === CardsLayout.FIXED_IMAGE_HEIGHT && showLabel && size !== CardSize.SMALL && (
				<Inline xcss={styles.fieldWrapperStyles}>
					<LabelButton
						labelId={labelId}
						labelValue={labelValue?.trimStart()}
						handleLabelChange={handleLabelChange}
						isLabelHiddenValue={isLabelHiddenValue}
						handleIsLabelHiddenChange={handleIsLabelHiddenChange}
						labelColorValue={labelColorValue}
						handleLabelColorChange={handleLabelColorChange}
					/>
				</Inline>
			)}
			<Flex xcss={styles.fieldWrapperStyles} direction="column">
				<Label htmlFor={titleId}>
					<PanelSubheader showUpdatedStyles>
						<FormattedMessage {...messages.titleFieldLabel} />
						<RecommendedWrapper>
							{' '}
							<FormattedMessage {...messages.recommendedFieldLabel} />
						</RecommendedWrapper>
					</PanelSubheader>
				</Label>
				<Textfield
					id={titleId}
					value={titleValue?.trimStart()}
					onChange={handleTitleOnChange}
					maxLength={255}
					isDisabled={isDisabled}
				/>
			</Flex>
			{showDescription && (
				<AIEventsInstrumentationProvider
					config={{
						product: 'confluence',
						subproduct: 'confluence',
						aiFeatureName: 'companyHubCardsAIDescription',
						proactiveGeneratedAI: 0,
						userGeneratedAI: 1,
						source: CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE,
						isAIFeature: 1,
					}}
				>
					<Flex xcss={styles.fieldWrapperStyles} direction="column">
						<Label htmlFor={descriptionId}>
							<PanelSubheader showUpdatedStyles>
								<FormattedMessage {...messages.descriptionFieldLabel} />
							</PanelSubheader>
						</Label>
						{confluencePageId && linkResourceType && (
							<AIDescriptionButtonWrapper
								confluencePageId={confluencePageId}
								linkResourceType={linkResourceType}
								isAILoading={isAILoading}
								setIsAILoading={setIsAILoading}
								setAIError={setAIError}
								setAIDescriptionContent={setAIDescriptionContent}
								setAIResponseState={setAIResponseState}
								analyticsSource={CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE}
							/>
						)}
						{isAILoading ? (
							<AIBorder loading useModalStyles>
								<Box xcss={styles.aiLoadingTextContainer}>
									<AIStreamLoading padding="space.250" />
								</Box>
							</AIBorder>
						) : (
							<>
								<TextArea
									id={descriptionId}
									rows={3}
									value={descriptionValue?.trimStart()}
									onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) => {
										setIsAILoading(undefined);
										handleDescriptionChange(event.currentTarget.value);
									}}
									maxLength={700}
									isDisabled={isDisabled}
									testId="card-description-field"
								/>
								{aiResponseState && isBoolean(isAILoading) && (
									<AIDescriptionStatusMessage
										responseState={aiResponseState}
										error={aiError}
										analyticsSource={CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE}
									/>
								)}
							</>
						)}
					</Flex>
				</AIEventsInstrumentationProvider>
			)}
		</Fragment>
	);
};
